/**
 * Campus Analytics API → widget-prop mappers.
 *
 * The campus `*-v1` endpoints are class-scoped twins of the head-office ones:
 * they return `data.classes[]` (not `campuses[]`), lead the KPI strip with
 * `enrolled_teachers`, and give per-class `read_share_percentage` instead of a
 * per-class trend. Shapes that are byte-identical to head office (adoption,
 * reading-mix, growth) reuse the head-office mappers directly.
 */

import {
  CampusDatum,
  KpiDatum,
  StudentDatum,
  TeacherDatum,
  fmt,
  fmtMins,
  pct,
} from "../headOffice/data";
import { initialsFrom } from "../headOffice/apiMappers";
import { ModalData } from "../headOffice/modals/modalBuilders";

/* identical-shape mappers reused verbatim from head office */
export {
  mapAdoption,
  mapReadingMix,
  mapGrowth,
  type AdoptionView,
  type GrowthView,
} from "../headOffice/apiMappers";

/* ------------------------------------------------------------------ *
 * Local helpers (mirror the private head-office ones)
 * ------------------------------------------------------------------ */

const round = (n: any): number => Math.round(Number(n) || 0);
const pctVal = (n: any): number => Math.round((Number(n) || 0) * 100) / 100;
const big = (n: any): string => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
  return v.toLocaleString("en-US");
};

interface ApiTrend {
  direction?: "up" | "down" | "neutral";
  percentage?: number;
  current?: number;
  previous?: number;
}
const trendPct = (t?: ApiTrend): number => {
  if (!t) return 0;
  const p = Math.abs(round(t.percentage));
  return t.direction === "down" ? -p : p;
};
const trendUp = (t?: ApiTrend): boolean => !t || t.direction !== "down";
const signed = (n: number, suffix = ""): string =>
  (n >= 0 ? "+" : "") + n + suffix;

const CAMPUS_DEFAULTS: CampusDatum = {
  name: "",
  initials: "",
  students: 0,
  books: 0,
  pages: 0,
  classes: 0,
  quiz: 0,
  adoption: 0,
  trend: 0,
  ageApprop: 0,
  completion: 0,
  active: 0,
  assigned: 0,
  inProgress: 0,
  overdue: 0,
  weekly: [],
  tLogins: 0,
  tAssignments: 0,
  tActive: 0,
  tTotal: 0,
  classesTop: [],
  studentsTop: [],
  teachersTop: [],
};
const makeCampus = (p: Partial<CampusDatum>): CampusDatum => ({
  ...CAMPUS_DEFAULTS,
  ...p,
});
const maxBooksOf = (list: CampusDatum[]): number =>
  Math.max(1, ...list.map((c) => c.books));

/* ------------------------------------------------------------------ *
 * 1. Dashboard → KPI strip (campus leads with teachers, not campuses)
 * ------------------------------------------------------------------ */

export const mapKpis = (data: any): KpiDatum[] => {
  const s = data?.stats ?? {};
  const absDelta = (t?: ApiTrend) =>
    signed(round(t?.current) - round(t?.previous));
  const kpi = (
    stat: any,
    label: string,
    icon: KpiDatum["icon"],
    windowed: boolean,
    valueFmt: (n: any) => string = fmt,
  ): KpiDatum => ({
    label,
    value: valueFmt(stat?.value),
    icon,
    delta: windowed ? signed(trendPct(stat?.trend), "%") : absDelta(stat?.trend),
    up: trendUp(stat?.trend),
  });

  // The 3rd tile is scope-dependent: a campus admin gets `enrolled_teachers`,
  // a (multi-campus) school admin gets `active_campuses`. Show whichever exists
  // so the same dashboard works for an individual school and a campus alike.
  const thirdTile = s.enrolled_teachers
    ? kpi(s.enrolled_teachers, "Total Teachers", "teacher", false)
    : kpi(s.active_campuses, "Total Campuses", "campus", false);

  const tiles: KpiDatum[] = [
    kpi(s.enrolled_students, "Total Students", "students", false),
    // screen time sits with the students it belongs to, not at the far end
    kpi(s.screen_time_minutes, "Total Screen Time", "time", true, fmtMins),
    thirdTile,
    kpi(s.books_read, "Total Books Read", "book", true),
    kpi(s.pages_read, "Total Pages Read", "pages", true, big),
  ];
  // `classrooms` only comes back on the campus / individual-school dashboard,
  // and it leads the strip: it is the unit everything else is counted within.
  if (s.classrooms) {
    tiles.unshift(kpi(s.classrooms, "Total Classrooms", "class", false));
  }
  // quizzes_attempted is present on the campus/individual-school dashboard
  if (s.quizzes_attempted) {
    tiles.push(kpi(s.quizzes_attempted, "Quizzes Attempted", "quiz", true));
  }
  return tiles;
};

/* ------------------------------------------------------------------ *
 * 2. Reader Class (list) — secondary bar = read-share %, no per-class trend
 * ------------------------------------------------------------------ */

export const mapReaderClasses = (
  data: any,
): { classes: CampusDatum[]; maxBooks: number } => {
  // class-shaped (campus admin) or campus-shaped (school admin) rows
  const rows = data?.classes ?? data?.campuses ?? [];
  const classes = rows.map((c: any) => {
    const name = c.class_name ?? c.campus_name;
    return makeCampus({
      campus_id: c.class_id ?? c.campus_id,
      name,
      initials: initialsFrom(name),
      teacher: c.teacher_name,
      students: Number(c.enrolled_students ?? c.active_enrolled_students) || 0,
      books: Number(c.total_books_read) || 0,
      // reused ReaderCampuses paints the secondary bar from `adoption`
      adoption: round(c.read_share_percentage ?? c.adoption_rate),
    });
  });
  return { classes, maxBooks: maxBooksOf(classes) };
};

/* ------------------------------------------------------------------ *
 * 3. Top Teachers — sub-label is the teacher's class(es)
 * ------------------------------------------------------------------ */

export const mapTopTeachers = (data: any): TeacherDatum[] =>
  (data?.teachers ?? []).map((t: any) => ({
    teacher_id: Number(t.teacher_id ?? t.id ?? t.user_id) || undefined,
    name: t.name,
    campus:
      (t.classes ?? []).map((c: any) => c.class_name).filter(Boolean).join(" · ") ||
      t.campus_name ||
      "No classroom",
    assigned: Number(t.assigned_books) || 0,
    completed: Number(t.completed_books) || 0,
    quiz: round(t.quiz?.passed_percentage),
  }));

/* ------------------------------------------------------------------ *
 * 4. Top Students — meta is the class name
 * ------------------------------------------------------------------ */

export const mapTopStudents = (data: any): StudentDatum[] =>
  (data?.students ?? []).map((s: any) => ({
    student_id: Number(s.student_id ?? s.id) || undefined,
    name: s.name,
    meta: [s.class_name, s.campus_name].filter(Boolean).join(" · ") || "No class",
    initials: initialsFrom(s.name),
    books: Number(s.completed_books) || 0,
    badges: Number(s.badges_count) || 0,
    pages: 0,
    time: "—",
    quiz: 0,
  }));

/* ------------------------------------------------------------------ *
 * 5. Book Content Analysis (per class)
 * ------------------------------------------------------------------ */

export interface ContentView {
  summary: {
    overallBooks: string;
    mostReadGenre: { name: string; pct: string };
    mostReadLanguage: { name: string; pct: string };
  };
  campuses: CampusDatum[];
  maxBooks: number;
}

export const mapContent = (data: any): ContentView => {
  const rows = data?.classes ?? data?.campuses ?? [];
  const campuses = rows.map((c: any) =>
    makeCampus({
      campus_id: c.class_id ?? c.campus_id,
      name: c.class_name ?? c.campus_name,
      books: Number(c.completed_books) || 0,
    }),
  );
  return {
    summary: {
      overallBooks: fmt(data?.total_completed_books ?? 0),
      mostReadGenre: {
        name: data?.top_genre?.name ?? "—",
        pct: data?.top_genre ? round(data.top_genre.percentage) + "%" : "—",
      },
      mostReadLanguage: {
        name: data?.most_read_language?.name ?? "—",
        pct: data?.most_read_language
          ? round(data.most_read_language.percentage) + "%"
          : "—",
      },
    },
    campuses,
    maxBooks: maxBooksOf(campuses),
  };
};

/* ------------------------------------------------------------------ *
 * 6. Class Comparison (per class)
 * ------------------------------------------------------------------ */

export const mapComparison = (data: any): CampusDatum[] => {
  const rows = data?.classes ?? data?.campuses ?? [];
  return rows.map((c: any) => {
    const enrolled =
      Number(c.enrolled_students ?? c.active_enrolled_students) || 0;
    const activeReaders = Number(c.active_readers) || 0;
    return makeCampus({
      campus_id: c.class_id ?? c.campus_id,
      name: c.class_name ?? c.campus_name,
      adoption: pctVal(c.adoption_rate),
      quiz: round(c.quiz?.passed_percentage),
      ageApprop: round(c.age_appropriate?.percentage),
      completion: round(c.book_completion_rate),
      // "Engagement" column — the API's own engagement_score when present,
      // otherwise derived as active readers / enrolled
      active:
        c.engagement_score != null
          ? pctVal(c.engagement_score)
          : enrolled
            ? round((activeReaders / enrolled) * 100)
            : pctVal(c.adoption_rate),
    });
  });
};

/* ------------------------------------------------------------------ *
 * 7. Teacher Adoption — per teacher (campus admin) OR per campus (school
 *    admin). Both are normalised into `quiz` (0–100), the % the widget bar
 *    reads, so the same TeacherAdoption widget renders either shape.
 * ------------------------------------------------------------------ */

export const mapTeacherAdoption = (data: any): CampusDatum[] => {
  if (data?.teachers) {
    return data.teachers.map((t: any) =>
      makeCampus({
        name: t.name,
        quiz: round(t.quiz?.passed_percentage),
        classes: (t.classes ?? []).length,
      }),
    );
  }
  // campus-shaped fallback: active-teacher share becomes the bar %
  return (data?.campuses ?? []).map((c: any) => {
    const active = Number(c.active_teachers) || 0;
    const total = Number(c.enrolled_teachers) || 0;
    return makeCampus({
      name: c.campus_name,
      quiz: total ? Math.round((active / total) * 100) : 0,
      tActive: active,
      tTotal: total,
      assigned: Number(c.assigned_books) || 0,
    });
  });
};

/* ------------------------------------------------------------------ *
 * 8. Class drill-down modal — assembled from the class detail + the three
 *    grade-v1 lenses (student / class-health / teacher).
 * ------------------------------------------------------------------ */

export const buildClassModalFromParts = (
  detail: any,
  studentsData: any,
  health: any,
  teacher: any,
): ModalData => {
  const adoption = pctVal(detail?.adoption_rate);
  const assigned = Number(detail?.assigned_books) || 0;
  const students: any[] = studentsData?.students ?? [];
  const maxS = Math.max(1, ...students.map((s) => Number(s.completed_books) || 0));

  return {
    kind: "campus",
    cert: true,
    title: detail?.class_name ?? "Classroom",
    subtitle: `${detail?.teacher?.name ?? "—"} · ${fmt(
      detail?.enrolled_students ?? 0,
    )} students · ${adoption}% adoption`,
    stats: [
      { value: fmt(detail?.total_books_completed ?? 0), label: "Books completed" },
      { value: adoption + "%", label: "Adoption" },
      { value: fmt(detail?.active_readers ?? 0), label: "Active readers" },
    ],
    stats2: [
      { value: fmt(assigned), label: "Assigned books", color: "var(--ho-ink)" },
      {
        value: fmt(detail?.in_progress_books ?? 0),
        label: "In progress",
        color: "#1897A6",
      },
      { value: detail?.overdue_books ?? 0, label: "Overdue", color: "#B42318" },
    ],
    tabs: [
      {
        label: "Students",
        listTitle: "The Reader Students",
        rows: students.map((s, i) => ({
          rank: i + 1,
          name: s.name,
          hasSub: false,
          value: (Number(s.completed_books) || 0) + " books",
          bar: pct(Number(s.completed_books) || 0, maxS),
        })),
      },
      {
        label: "Classroom health",
        listTitle: "Reading health checks",
        rows: [
          {
            rank: 1,
            name: "Age-appropriate reading",
            hasSub: false,
            value: round(health?.age_appropriate_reading_percentage) + "%",
            bar: round(health?.age_appropriate_reading_percentage) + "%",
          },
          {
            rank: 2,
            name: "Assignment completion",
            hasSub: false,
            value: round(health?.assignment_completion_percentage) + "%",
            bar: round(health?.assignment_completion_percentage) + "%",
          },
          {
            rank: 3,
            name: "Active readers",
            hasSub: false,
            value: round(health?.active_readers_percentage) + "%",
            bar: round(health?.active_readers_percentage) + "%",
          },
          {
            rank: 4,
            name: "Overdue titles",
            hasSub: true,
            sub: "books past their return date",
            value: health?.overdue_books ?? 0,
            bar: pct(Number(health?.overdue_books) || 0, 20),
          },
        ],
      },
      {
        label: "Teacher",
        listTitle: "Classroom teacher performance",
        rows: [
          {
            rank: 1,
            name: teacher?.teacher_name ?? detail?.teacher?.name ?? "Classroom teacher",
            hasSub: true,
            sub: `${fmt(teacher?.total_assigned_books ?? 0)} assigned · ${round(
              teacher?.quiz_pass_percentage,
            )}% quiz pass`,
            value: round(teacher?.book_completion_rate) + "%",
            bar: round(teacher?.book_completion_rate) + "%",
          },
        ],
      },
    ],
  };
};

/* ------------------------------------------------------------------ *
 * 9. Student drill-down modal (from the top-students row — the campus API
 *    has no per-student detail endpoint, so show what the row carries).
 * ------------------------------------------------------------------ */

export const buildStudentModalFromRow = (s: StudentDatum): ModalData => ({
  kind: "student",
  cert: false,
  title: s.name,
  subtitle: s.meta,
  stats: [
    { value: fmt(s.books), label: "Books read" },
    { value: fmt(s.badges), label: "Badges earned" },
  ],
  stats2: [],
  tabs: [
    {
      label: "Progress",
      listTitle: "Progress highlights",
      rows: [
        {
          rank: 1,
          name: "Books completed",
          hasSub: false,
          value: fmt(s.books),
          bar: "100%",
        },
        {
          rank: 2,
          name: "Badges earned",
          hasSub: false,
          value: fmt(s.badges),
          bar: pct(s.badges, Math.max(1, s.badges)),
        },
      ],
    },
  ],
});
